import React, { useState, useEffect } from 'react';
import DaycareTransferPackageModal from './modals/DaycareTransferPackageModal';
import { Gate } from './AccessStudio';

const AgeWarningsAlert = ({ academicYear, apiCall, showSuccess, showError }) => {
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);

  useEffect(() => {
    loadWarnings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear]);

  const loadWarnings = async () => {
    try {
      const data = await apiCall(`/daycare/warnings?academicYear=${academicYear}`);
      setWarnings(data || []);
    } catch (error) {
      console.error('Error loading warnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveWarning = async (warningId, notes = '') => {
    try {
      await apiCall(`/daycare/warnings/${warningId}/resolve`, {
        method: 'PUT',
        body: JSON.stringify({ notes })
      });
      showSuccess('Warning resolved successfully');
      loadWarnings(); // Reload warnings
    } catch (error) {
      showError('Error resolving warning: ' + error.message);
    }
  };

  const getWarningColor = (type) => {
    switch (type) {
      case 'AgedOut':
        return 'red';
      case 'AgingOut':
        return 'yellow';
      case 'TooYoung':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getWarningIcon = (type) => {
    switch (type) {
      case 'AgedOut':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'AgingOut':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  if (loading) {
    return null; // Don't show loading state for warnings
  }

  if (warnings.length === 0) {
    return null; // Don't show anything if no warnings
  }

  return (
    <div className="mb-6">
      <div className={`border-l-4 ${
        warnings.some(w => w.WarningType === 'AgedOut') ? 'border-red-500 bg-red-50' :
        warnings.some(w => w.WarningType === 'AgingOut') ? 'border-yellow-500 bg-yellow-50' :
        'border-orange-500 bg-orange-50'
      } p-4 rounded-r-lg shadow-sm`}>
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`${
              warnings.some(w => w.WarningType === 'AgedOut') ? 'text-red-600' :
              warnings.some(w => w.WarningType === 'AgingOut') ? 'text-yellow-600' :
              'text-orange-600'
            }`}>
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Age Warnings ({warnings.length})
              </h3>
              <p className="text-sm text-gray-600">
                Children who have outgrown or are outgrowing their current packages
              </p>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg 
              className={`w-5 h-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Warnings List */}
        {expanded && (
          <div className="mt-4 space-y-3">
            {warnings.map((warning) => {
              const color = getWarningColor(warning.WarningType);
              const bgColor = `bg-${color}-100`;
              const textColor = `text-${color}-800`;
              const borderColor = `border-${color}-200`;

              return (
                <div
                  key={warning.WarningID}
                  className={`p-4 bg-white border-2 ${borderColor} rounded-lg`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className={textColor}>
                        {getWarningIcon(warning.WarningType)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="font-semibold text-gray-900">
                            {warning.ChildName}
                          </p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor}`}>
                            {warning.WarningType === 'AgedOut' ? 'Too Old' :
                             warning.WarningType === 'AgingOut' ? 'Aging Out' :
                             'Too Young'}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-2">
                          {warning.WarningMessage}
                        </p>

                        <div className="flex items-center space-x-4 text-xs text-gray-600">
                          <span>Family: {warning.FamilyID}</span>
                          <span>•</span>
                          <span>Phone: {warning.PrimaryPhone}</span>
                          {warning.DaysUntilExpiry > 0 && (
                            <>
                              <span>•</span>
                              <span className="font-medium text-orange-600">
                                Grace period: {warning.DaysUntilExpiry} days left
                              </span>
                            </>
                          )}
                          {warning.DaysUntilExpiry <= 0 && (
                            <>
                              <span>•</span>
                              <span className="font-medium text-red-600">
                                Grace period expired!
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <Gate permission="daycare.management.edit" label="Warning actions">
                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => {
                          setSelectedWarning(warning);
                          setShowTransferModal(true);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
                      >
                        Transfer Package
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Mark this warning as resolved?')) {
                            resolveWarning(warning.WarningID, 'Resolved by admin');
                          }
                        }}
                        className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded hover:bg-gray-300 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                    </Gate>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transfer Package Modal */}
      <DaycareTransferPackageModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setSelectedWarning(null);
        }}
        onSuccess={() => {
          loadWarnings();
          setShowTransferModal(false);
          setSelectedWarning(null);
        }}
        warning={selectedWarning}
        apiCall={apiCall}
        showError={showError}
        showSuccess={showSuccess}
      />
    </div>
  );
};

export default AgeWarningsAlert;